/**
 * Environment configuration utility
 * Centralizes environment variable handling with validation
 */

// Define required environment variables 
const REQUIRED_ENV_VARS = [
  'REACT_APP_SUPABASE_URL',
  'REACT_APP_SUPABASE_ANON_KEY'
] as const;

// Define optional environment variables with defaults
interface EnvDefaults {
  REACT_APP_API_URL: string;
  REACT_APP_UPLOAD_URL: string;
  REACT_APP_IMAGES_URL: string;
}

const ENV_DEFAULTS: EnvDefaults = {
  REACT_APP_API_URL: 'https://api.islamicdeals.com',
  REACT_APP_UPLOAD_URL: 'https://upload.islamicdeals.com',
  REACT_APP_IMAGES_URL: 'https://images.islamicdeals.com'
};

// Environment variable types
type RequiredEnvVars = typeof REQUIRED_ENV_VARS[number];
type OptionalEnvVars = keyof EnvDefaults;
type EnvVars = RequiredEnvVars | OptionalEnvVars;

// Validate environment variables
const validateEnv = () => {
  const missingVars = REQUIRED_ENV_VARS.filter(
    (key) => !process.env[key]
  );
  
  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}. ` +
      'Please check your .env file or deployment configuration.'
    );
  }
};

// Safely access environment variables with proper fallbacks
// and type safety
const getEnvVar = <T extends EnvVars>(key: T): string => {
  // For required variables, process.env must have them after validation
  if (REQUIRED_ENV_VARS.includes(key as RequiredEnvVars)) {
    return process.env[key] as string;
  }
  
  // For optional variables, use defaults if not provided
  const optionalKey = key as OptionalEnvVars;
  return process.env[key] || ENV_DEFAULTS[optionalKey];
};

// Run validation on module import
validateEnv();

// Export environment configuration
export const config = {
  // Supabase Configuration
  supabaseUrl: getEnvVar('REACT_APP_SUPABASE_URL'),
  supabaseAnonKey: getEnvVar('REACT_APP_SUPABASE_ANON_KEY'),
  
  // API URLs
  apiUrl: getEnvVar('REACT_APP_API_URL'),
  uploadUrl: getEnvVar('REACT_APP_UPLOAD_URL'),
  imagesUrl: getEnvVar('REACT_APP_IMAGES_URL'),
  
  // Environment information
  isDevelopment: process.env.NODE_ENV === 'development',
  isProduction: process.env.NODE_ENV === 'production',
  isTest: process.env.NODE_ENV === 'test',
  
  // Feature flags
  enableAnalytics: process.env.NODE_ENV === 'production',
  enableDetailedLogs: process.env.NODE_ENV !== 'production',
  enableDebugFeatures: process.env.NODE_ENV !== 'production',
  
  // Security settings
  maxUploadSizeMB: 5,
  allowedFileTypes: ['image/jpeg', 'image/png', 'image/webp'],
};

export default config;